import React from 'react';
import { motion } from 'framer-motion';
import { Apple, Play, Star, Zap } from 'lucide-react';
import Button from './ui/Button';
import { fadeInUp, stagger } from '../lib/animations';

// Seção de CTA de download com fundo gradiente âmbar e destaque total
const DownloadSection: React.FC = () => (
  <section
    id="download"
    className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-amber-400 to-orange-500 py-20 sm:py-28"
  >
    {/* Decorações de fundo */}
    <div className="pointer-events-none absolute top-0 right-0 h-80 w-80 rounded-full bg-orange-300/30 blur-3xl" />
    <div className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 rounded-full bg-yellow-300/30 blur-3xl" />

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {/* Badge */}
        <motion.div variants={fadeInUp} className="mb-6 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/25 px-4 py-1.5 text-xs font-semibold tracking-wide text-gray-900">
            <Zap className="h-3.5 w-3.5" strokeWidth={2} />
            Disponvel · Android e iOS
          </span>
        </motion.div>

        {/* Título */}
        <motion.h2
          variants={fadeInUp}
          className="mx-auto max-w-3xl text-4xl font-bold leading-[1.1] tracking-tight text-gray-900 sm:text-5xl lg:text-[3.2rem]"
        >
          Baixe agora e comece a economizar hoje
        </motion.h2>

        {/* Subtítulo */}
        <motion.p
          variants={fadeInUp}
          className="mx-auto mt-5 max-w-2xl text-base text-gray-800 sm:text-lg"
        >
          Aquilo que você não mede, você não gere — e o que você não gere, você perde.
          Registre cada abastecimento, acompanhe seu consumo real e tome o controle dos seus gastos com o carro antes que eles controlem você.
        </motion.p>

        {/* Botões das lojas */}
        <motion.div
          variants={fadeInUp}
          className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Button
            href="https://play.google.com/store/apps/details?id=info.marcussoftware.gas2&hl=pt_BR&utm_source=lp&utm_medium=cta&utm_campaign=gas2_download"
            variant="dark"
            size="lg"
            icon={Play}
            target="_blank"
          >
            Google Play
          </Button>
          <Button
            href="https://apps.apple.com/br/app/gas2-controle-de-combust%C3%ADvel/id6755345109?utm_source=lp&utm_medium=cta&utm_campaign=gas2_download"
            variant="outline"
            size="lg"
            icon={Apple}
            target="_blank"
            className="!border-gray-900/20 !bg-white/30 !text-gray-900 hover:!bg-white hover:!text-gray-900"
          >
            App Store
          </Button>
        </motion.div>

        {/* Cards de plataforma */}
        <motion.div
          variants={fadeInUp}
          className="mx-auto mt-12 flex flex-col items-stretch justify-center gap-4 sm:flex-row sm:max-w-2xl"
        >
          {/* Android */}
          <div className="flex flex-1 items-start gap-4 rounded-3xl border border-white/50 bg-white/30 p-5 text-left backdrop-blur-sm">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
              <Play className="h-5 w-5 text-gray-900" strokeWidth={2} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Android · Google Play</p>
              <div className="mt-1 flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-amber-500 text-amber-500" strokeWidth={0} />
                ))}
                <span className="ml-1 text-xs font-semibold text-gray-800">4,9★</span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-gray-700">
                Junte-se aos utilizadores que avaliaram a nossa app com 4,9 estrelas.
              </p>
            </div>
          </div>

          {/* iOS */}
          <div className="flex flex-1 items-start gap-4 rounded-3xl border border-white/50 bg-white/30 p-5 text-left backdrop-blur-sm">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm">
              <Apple className="h-5 w-5 text-gray-900" strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">iOS · App Store</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                ✓ Disponível agora
              </p>
              <p className="mt-1 text-xs leading-relaxed text-gray-700">
                Migrámos do TestFlight direto para a App Store pública — faça o download e deixe o seu feedback!
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

export default DownloadSection;
